import { GameMode } from '@nolimitcity/slot-game/bin/flowcontrol/GameMode';
import { StateHandler } from '../../../core/utils/StateHandler';
import { Presentation } from '../../../core/gameflow/controller/Presentation';
import { WinGameData } from '../../server/data/GameDataModels';
import { GameData } from '../../server/data/GameData';
import { GameConfig } from '../../config/base/GameConfig';
import { IWinData } from '../../server/base/IData';
import { SlotGame } from '@nolimitcity/slot-game/bin/SlotGame';
import { BeheadedSymbol } from '../../reels/symbol/BeheadedSymbol';
import { BeheadedEngineManager } from '../../reels/BeheadedEngineManager';
import { Beheaded } from '../../Beheaded';
import { BeheadedSound } from '../../sound/BeheadedSound';
import { gsap, Power2 } from 'gsap';
import { GameAssets } from '../../GameAssets';
import { BitmapText } from 'pixi.js';
import { GameSetting } from '../../../core/setting/GameSetting';

class WinState extends StateHandler {
    public static readonly WIN: string = 'win';
    public static readonly SMALL_WIN: string = 'small_win';
    public static readonly NORMAL: string = 'normal';
    public static readonly BIGWIN: string = 'bigwin';
    public static readonly END: string = 'end';
    public static readonly DONE: string = 'done';
}

export class NormalWinPresentation extends Presentation {
    private winData: WinGameData;
    private _winState: WinState = new WinState();
    private winAmount: number = 0;
    private isBigWin: boolean = false;
    private winIndx: number = 0;
    private winConfigDurr: number = 0;
    private lastNormalIndex: number = 0;

    private noWinAlpha: number = 0.45;

    private skipped: boolean = false;
    private skipFunc: Function;
    private isBelowStake: boolean = false;
    private isTotalWin: boolean = false;

    private currentLevel: number;

    private cutIndx: number[] = [];

    private swPosMap:boolean[][];
    private swSize:number;

    public checkForActivation(data: GameData): boolean {
        return !!data.Win.singleWin;
    }

    public initialization(): void {
        this.lastNormalIndex = GameConfig.WIN_RATIOS.normalWin.length - 1;
    }

    preStart(): void {
        this.skipped = false;
        Beheaded.counter.changeToBigWinFont(false);
    }

    public setGamedData(data: GameData): void {
        this.winData = data.Win;
        this.isBigWin = this.winData.isBigWin;
        this._winState.set(WinState.WIN);
        this.isTotalWin = data.mode !== GameMode.NORMAL && this.winData!.totalWin != this.winData.singleWin;
        this.currentLevel = data.currentLevel;
        this.winAmount = !this.isBigWin
            ? this.winData.singleWin
            : this.winData.playedBetValue * GameConfig.WIN_RATIOS.bigWin[0].ratio;

        this.winIndx = GameConfig.WIN_RATIOS.normalWin.findIndex((indx) => indx.ratio == this.winData.winRatioConfig.ratio);
        if (this.winIndx == -1) this.winIndx = this.lastNormalIndex;

        this.winConfigDurr = GameConfig.WIN_RATIOS.normalWin[this.winIndx].duration;

        this.swPosMap = data.Reel.SWBoolMap;
        this.swSize = data.Reel.SWSize;

        this.cutIndx = data.raw.cutIndex;
        this.isBelowStake = data.Win.isWinBelowStake!();
    }

    createPresentation(skip: Function): void {
        this.skipFunc = skip;

        switch (this._winState.value) {
            case WinState.WIN:
                this.skipFunc(!this.isBigWin);
                this.winPositions();
                this._winState.set(this.isBigWin ? WinState.END : WinState.NORMAL);
                if (this.winData.winRatioConfig.ratio === 0) {
                    this._winState.set(WinState.END);
                }
                if (this.isBelowStake) this._winState.set(WinState.END);
                break;
            case WinState.NORMAL:
                this.skipFunc(!this.isBigWin);
                this.addWinCountUpToTimeline();
                this._winState.set(WinState.END);
                break;
            case WinState.END:
                this.skipFunc(false);
                this.ending();
                this._winState.set(WinState.DONE);
                this.PresentationState.setInactive();
                break;
            default:
                if (!this.isBigWin) this.timeline.to(this, { duration: 0.15 });
                this.PresentationState.setInactive();
                break;
        }
    }

    protected winPositions(): void {
        const winGrps: IWinData[] = this.winData.winData!;
        let startPos: number = 0;

        if ((this.winIndx && winGrps.length == 1 && !GameSetting.showAllIndiviWins)) {
            return;
        }

        let showMiniText: boolean = false;
        let longestAnimation: number = 0;

        const groupDelay: number = GameSetting.isFastSpin ? 0.2 : 0.225;
        const reelDelay: number = GameSetting.isFastSpin ? 0 : 0.075;

        for (let groups = 0; groups < winGrps.length; groups++) {
            const winData: IWinData = winGrps[groups];
            let SWShowHit:boolean = true;
            showMiniText = !Beheaded.apiPlugin.gameClientConfiguration.belowStakeWinRestriction;

            for (let reel = 0; reel < winData.reelWinPositions.length; reel++) {
                let posRow: boolean[] = winData.reelWinPositions[reel];
                for (let row = 0; row < posRow.length; row++) {
                    const sym: BeheadedSymbol = BeheadedEngineManager.getReel(reel).getSymbol(row)!;
                    if (posRow[row] && sym && !sym.isScatter) {

                        longestAnimation = Math.max(longestAnimation, sym.fetchWinDuration(false));

                        if(this.swPosMap[reel][row] && SWShowHit){
                            this.timeline.add(() => { Beheaded.souldWildController.showWin(sym.fetchWinDuration(false),this.swSize)},startPos);
                            SWShowHit = false;
                        }

                        this.timeline.add(() => {
                            sym.playWin(false, reelDelay);
                        }, startPos);

                        if (showMiniText && reel == winData.length - 1 && !this.isBelowStake) {

                            this.timeline.add(() => {
                                this.addWinWaysText(winData, reel, row);
                            }, startPos);

                            showMiniText = false;
                        }
                    }
                }
                if (reel != winData.length - 1) startPos += reelDelay;
            }
            if (groups != winGrps.length) startPos += groupDelay;
        }

        this.timeline.to(this, { duration: !GameSetting.isFastSpin ? longestAnimation / 3.5 : 0.1 }, startPos);
    }

    protected addWinWaysText(winData: IWinData, rowId: number, symId: number): gsap.core.Timeline {
        let container: PIXI.Container = new PIXI.Container();
        container.alpha = 0;
        let text: BitmapText = new BitmapText(winData.multipliedAmount.toFixed(2), {
            fontName: GameAssets.BITMAP_FONT_NORMAL_WIN_COUNT_UP,
            fontSize: 32,
        });
        text.anchor = new PIXI.Point(0.5, 0.5);
        let waysText: string = winData.numberOfCombinations + ' ' + (winData.numberOfCombinations > 1 ? 'Ways' : 'Way');
        let ways: PIXI.Text = new PIXI.Text(waysText, {
            dropShadow: true,
            dropShadowAngle: 7.5,
            fill: 'white',
            fontFamily: 'Helvetica',
            fontSize: 20,
            fontStyle: 'italic',
            fontWeight: 'bold',
            miterLimit: 0,
            strokeThickness: 3,
        });
        ways.anchor.set(0.5, 0.5);
        let symbolPosition: PIXI.Point = Beheaded.reelEngineManager.getPosition(rowId, symId);
        container.position.set(symbolPosition.x, symbolPosition.y);
        ways.position.set(0, 25);
        let yPos: number = container.y;
        container.addChild(text, ways);

        Beheaded.overlayAnim.addChild(container);
        
        let tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                Beheaded.overlayAnim.removeChild(container);
            },
        });

        tl.fromTo(container, { alpha: 0 }, { alpha: 1, duration: 0.1 });
        tl.to(container, { y: yPos - 40, duration: 0.8 });
        tl.to(container, { alpha: 0, duration: 0.3, ease: 'power2.easeOut' });
        return tl;
    }

    protected addWinCountUpToTimeline(): void {
        let countUpDuration: number = this.winConfigDurr;

        countUpDuration = countUpDuration ? countUpDuration : 0.2;

        let maxLength: number = this.getMaxLengthOfWinData(this.winData.allBetWayWinData.reelWinPositions);
        let offset: number = countUpDuration / maxLength;

        let shouldLoop: boolean = this.winData.winData.length > 1;

        for (let reel = 0; reel < this.winData.allBetWayWinData.reelWinPositions.length; reel++) {
            for (let row = 0; row < this.winData.allBetWayWinData.reelWinPositions[reel].length; row++) {
                const hits: boolean = this.winData.allBetWayWinData.reelWinPositions[reel][row];
                const sym: BeheadedSymbol = BeheadedEngineManager.getReel(reel).getSymbol(row)!;
                if (hits && sym && !sym.isScatter) {
                    this.timeline.add(() => {
                        sym.playWin(shouldLoop, offset);
                    }, offset * reel);
                }
            }
        }
        this.timeline.add(Beheaded.counter.play(0, this.winAmount, countUpDuration, this.winData.totalWinCombination),0);

        this.timeline.add(() => {
            if (this.winIndx > 1) {
                Beheaded.soundPlayer.playSound(BeheadedSound.COUNT_UP);
            }
        }, 0);

        if(this.winIndx >= 2){
            
            this.timeline.add(() => {
                Beheaded.counter.ShakeItBaby(2, countUpDuration);
            }, 0);
            if(this.winIndx == 3)
                this.timeline.add(() => {
                    // Beheaded.overlayAnim.bounceScreen(1,0.99);
                    Beheaded.backgroundCharacter.showSmokeLevelUp(this.currentLevel,false);
                }, countUpDuration);
        }

        this.timeline.add(() => {
            if (this.winIndx > 1) {
                Beheaded.counter.bounceHit(this.winIndx*3, this.winIndx > 1 ?  0.4 : 0.15, true);
            }
            Beheaded.soundPlayer.stopSound(BeheadedSound.COUNT_UP);
            Beheaded.soundPlayer.playSound(BeheadedSound.COUNT_UP_ENDS[this.winIndx]);



        }, countUpDuration);

        if (!this.isBelowStake) {
            this.timeline.add(() => {
                Beheaded.counter.showWays(0.4);
            }, countUpDuration )
        }
        if (this.winIndx)
            this.timeline.to(this,{duration:0.2});

    }

    private getMaxLengthOfWinData(data: boolean[][]): number {
        let length: number = 0;
        data.forEach((reelData: boolean[]) => {
            let isWinExist: boolean = false;
            reelData.forEach((isWin) => {
                if (!isWinExist && isWin) {
                    length++;
                    isWinExist = true;
                }
            });
        });
        return length;
    }

    public skip(): void {
        if (this._winState.equal(WinState.DONE)) this.skipped = true;
        super.skip();
    }

    public ending(): void {
        if (!this.isBigWin) {
            SlotGame.keypadPlugin.setWin(this.winData!.totalWin, this.isTotalWin, this.isBelowStake);
            if (this.winIndx) this.timeline.to(this, this.skipped ? 0.15 : 0.4, {}, 0);
        }
    }

    public done(): void {
        
        BeheadedEngineManager.RAll((reel) => {
            reel.setCutIndicator(this.cutIndx[reel.reelID]);
        });

        if (this.skipped || !this.winIndx || GameSetting.isFastSpin)
            Beheaded.counter.stop(this.winAmount, 0.25, 0.25, true);
        else Beheaded.counter.stop(this.winAmount, 0.5, 1, true);
    }
}
